import React from 'react';
import { motion } from 'framer-motion';
import MainLayout from '../components/layout/MainLayout';
import { useTheme } from '../context/ThemeContext';
import {
  Briefcase, Calendar, TrendingUp, Users, FileText, Clock,
  ArrowUpRight, ArrowDownRight, Scale, Gavel, AlertCircle,
  CheckCircle2, Timer, IndianRupee
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const stats = [
  { id: 1, label: 'Active Cases', value: '24', change: '+3', trend: 'up', icon: Briefcase, color: 'from-blue-500 to-cyan-500' },
  { id: 2, label: "Today's Hearings", value: '3', change: '0', trend: 'neutral', icon: Calendar, color: 'from-purple-500 to-pink-500' },
  { id: 3, label: 'Win Rate', value: '78%', change: '+5%', trend: 'up', icon: TrendingUp, color: 'from-green-500 to-emerald-500' },
  { id: 4, label: 'Total Clients', value: '156', change: '+12', trend: 'up', icon: Users, color: 'from-orange-500 to-amber-500' },
];

const revenueData = [
  { month: 'Jan', revenue: 45000 },
  { month: 'Feb', revenue: 52000 },
  { month: 'Mar', revenue: 48000 },
  { month: 'Apr', revenue: 61000 },
  { month: 'May', revenue: 55000 },
  { month: 'Jun', revenue: 72000 },
];

const caseTypeData = [
  { name: 'Civil', value: 35, color: '#2D8CFF' },
  { name: 'Criminal', value: 25, color: '#D4AF37' },
  { name: 'Corporate', value: 20, color: '#10B981' },
  { name: 'Family', value: 15, color: '#8B5CF6' },
  { name: 'Other', value: 5, color: '#F59E0B' },
];

const todayHearings = [
  { id: 1, case: 'WP/1234/2024', court: 'Delhi High Court', time: '10:30 AM', client: 'Sharma Industries', status: 'upcoming' },
  { id: 2, case: 'CS/567/2024', court: 'Tis Hazari Court', time: '2:00 PM', client: 'Verma & Sons', status: 'upcoming' },
  { id: 3, case: 'CRL/890/2024', court: 'Saket Court', time: '4:30 PM', client: 'Gupta Holdings', status: 'upcoming' },
];

const recentActivity = [
  { id: 1, type: 'order', title: 'Order uploaded', desc: 'WP/1234/2024 - Delhi HC', time: '2 mins ago', icon: FileText },
  { id: 2, type: 'hearing', title: 'Hearing completed', desc: 'CS/567/2024 - Next: 15 Jan', time: '1 hour ago', icon: Gavel },
  { id: 3, type: 'client', title: 'New client added', desc: 'XYZ Corporation', time: '3 hours ago', icon: Users },
  { id: 4, type: 'document', title: 'Draft approved', desc: 'Agreement for ABC Ltd', time: '5 hours ago', icon: CheckCircle2 },
];

const Dashboard = () => {
  const { isDark } = useTheme();

  return (
    <MainLayout title="Dashboard" subtitle="Welcome back, Adv. VSKR">
      {/* Stats Grid */}
      <div className="grid grid-cols-4 gap-6 mb-8">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ scale: 1.02, y: -5 }}
            className="relative overflow-hidden rounded-2xl p-6 cursor-pointer"
            style={{
              background: isDark ? 'rgba(30, 41, 59, 0.8)' : 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(20px)',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`
            }}
          >
            {/* Gradient Top Border */}
            <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${stat.color}`} />
            
            {/* Glow Effect */}
            <motion.div
              className={`absolute -top-10 -right-10 w-32 h-32 rounded-full bg-gradient-to-r ${stat.color} opacity-20 blur-2xl`}
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 3, repeat: Infinity }}
            />
            
            <div className="flex items-start justify-between relative z-10">
              <div>
                <p className="text-sm text-gray-500 mb-1">{stat.label}</p>
                <h3 className="text-3xl font-bold">{stat.value}</h3>
                <div className="flex items-center gap-1 mt-2">
                  {stat.trend === 'up' ? (
                    <ArrowUpRight className="w-4 h-4 text-green-500" />
                  ) : stat.trend === 'down' ? (
                    <ArrowDownRight className="w-4 h-4 text-red-500" />
                  ) : null}
                  <span className={`text-sm ${
                    stat.trend === 'up' ? 'text-green-500' : 
                    stat.trend === 'down' ? 'text-red-500' : 'text-gray-500'
                  }`}>{stat.change}</span>
                </div>
              </div>
              <div className={`p-3 rounded-xl bg-gradient-to-r ${stat.color}`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-3 gap-6">
        {/* Revenue Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="col-span-2 glass-card p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold">Revenue Overview</h3>
              <p className="text-sm text-gray-500">Monthly earnings trend</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold">₹72,000</span>
              <span className="px-2 py-1 text-xs bg-green-500/20 text-green-500 rounded-lg">+18%</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={revenueData}>
              <defs>
                <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2D8CFF" stopOpacity={0.4}/>
                  <stop offset="95%" stopColor="#2D8CFF" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#E5E7EB'} />
              <XAxis dataKey="month" stroke={isDark ? '#94A3B8' : '#6B7280'} />
              <YAxis stroke={isDark ? '#94A3B8' : '#6B7280'} />
              <Tooltip
                contentStyle={{
                  background: isDark ? '#1E293B' : '#fff',
                  border: 'none',
                  borderRadius: '12px',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
                }}
              />
              <Area type="monotone" dataKey="revenue" stroke="#2D8CFF" strokeWidth={3} fill="url(#revenueGradient)" />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Case Types Pie Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="glass-card p-6"
        >
          <h3 className="text-lg font-semibold mb-2">Case Distribution</h3>
          <p className="text-sm text-gray-500 mb-4">By practice area</p>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={caseTypeData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
              >
                {caseTypeData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 gap-2 mt-4">
            {caseTypeData.map((item) => (
              <div key={item.name} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ background: item.color }} />
                <span className="text-xs">{item.name} ({item.value}%)</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Today's Hearings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="col-span-2 glass-card p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500">
                <Calendar className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Today's Hearings</h3>
                <p className="text-sm text-gray-500">3 hearings scheduled</p>
              </div>
            </div>
            <button className="text-sm text-blue-500 hover:underline">View Calendar</button>
          </div>
          <div className="space-y-4">
            {todayHearings.map((hearing, index) => (
              <motion.div
                key={hearing.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.7 + index * 0.1 }}
                whileHover={{ scale: 1.01, x: 5 }}
                className="flex items-center justify-between p-4 rounded-xl transition-all"
                style={{
                  background: isDark ? 'rgba(45, 140, 255, 0.1)' : 'rgba(45, 140, 255, 0.05)',
                  border: `1px solid ${isDark ? 'rgba(45, 140, 255, 0.2)' : 'rgba(45, 140, 255, 0.1)'}`
                }}
              >
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500">
                    <Scale className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold">{hearing.case}</p>
                    <p className="text-sm text-gray-500">{hearing.court}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-blue-500">{hearing.time}</p>
                  <p className="text-sm text-gray-500">{hearing.client}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Recent Activity */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="glass-card p-6"
        >
          <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
          <div className="space-y-4">
            {recentActivity.map((activity, index) => (
              <motion.div
                key={activity.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 + index * 0.1 }}
                className="flex items-start gap-3"
              >
                <div className="p-2 rounded-lg bg-gray-500/10">
                  <activity.icon className="w-4 h-4" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">{activity.title}</p>
                  <p className="text-xs text-gray-500">{activity.desc}</p>
                </div>
                <span className="text-xs text-gray-400">{activity.time}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </MainLayout>
  );
};

export default Dashboard;
